import React from 'react';
import { Button } from 'components/shadcn/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'components/shadcn/ui/select';
import { LoadingSpinner } from 'components/shadcn/ui/loadingSpinner';
import {
    Alert,
    AlertTitle,
    AlertDescription,
} from 'components/shadcn/ui/alert';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { useJwtToken } from 'helper';
import { useToast } from 'components/shadcn/hooks/use-toast';
import {
    useAllFirmwarePackages,
    pushFirmwareSideload,
    getPresignedFirmwareUrl,
} from './service';

const FirmwareSideload = () => {
    const { Thing_ID } = useGetIDsFromURL();
    const accessToken = useJwtToken();
    const { toast } = useToast();
    const { data: firmwarePackages, isLoading } = useAllFirmwarePackages();
    const [selectedFirmware, setSelectedFirmware] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const pushFirmware = async () => {
        setIsSubmitting(true);
        try {
            const downloadUrl = await getPresignedFirmwareUrl(
                selectedFirmware,
                accessToken
            );
            await pushFirmwareSideload(Thing_ID, downloadUrl, accessToken);
            toast({ title: 'Firmware push initiated' });
        } catch (error) {
            toast({
                title: 'Firmware push failed',
                description: error.message,
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-4">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="h-full space-y-4 bg-muted/40 p-4 w-full lg:w-80">
            <Alert variant="warning">
                <AlertTitle>Warning</AlertTitle>
                <AlertDescription>
                    This will force push firmware directly onto the device. Any
                    firmware version currently on the device will be overwritten
                    and this action cannot be undone.
                </AlertDescription>
            </Alert>
            <Select onValueChange={setSelectedFirmware}>
                <SelectTrigger>
                    <SelectValue placeholder="Select a firmware package" />
                </SelectTrigger>
                <SelectContent>
                    {firmwarePackages?.map((pkg) => (
                        <SelectItem key={pkg.eTag} value={pkg.s3Url}>
                            {pkg.filename}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <div className="flex w-full justify-end">
                <Button
                    className="ml-auto dark:border-slate-800"
                    variant="outline"
                    onClick={() => pushFirmware()}
                    disabled={!selectedFirmware || isSubmitting}
                >
                    {isSubmitting ? (
                        <LoadingSpinner className="h-4 w-4 border-2" />
                    ) : (
                        'Push Firmware'
                    )}
                </Button>
            </div>
        </div>
    );
};

export default FirmwareSideload;
